import { useState } from "react";
import { ArrowRight, CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { requestProspectus } from "@/lib/api";

export const ProspectusDialog = ({
  children,
  triggerText = "Download the Personal Clarity prospectus",
  triggerClassName = "group inline-flex items-center gap-3 bg-weave-yellow text-weave-ink hover:bg-[#e5ab38] transition-colors duration-300 rounded-full px-8 py-4 font-medium",
  testId = "prospectus-trigger",
}) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", email: "", marketing_consent: false });
  const [busy, setBusy] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");

  const startDownload = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = "the-weave-founder-academy-personal-clarity-prospectus.pdf";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Please add your email so we can send you the prospectus.");
      return;
    }
    setBusy(true);
    try {
      const res = await requestProspectus(form);
      setDownloadUrl(res.download_url);
      startDownload(res.download_url);
      toast.success("Your prospectus download has started.");
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <button type="button" data-testid={testId} className={triggerClassName}>
            {triggerText}
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-white border-weave-line rounded-2xl" data-testid="prospectus-dialog">
        <DialogHeader>
          <DialogTitle className="text-weave-ink font-rubik">Download the Personal Clarity prospectus</DialogTitle>
          <DialogDescription className="text-weave-slate">
            Share your email and we’ll send you the prospectus for the ten-week Personal Clarity phase.
          </DialogDescription>
        </DialogHeader>
        {downloadUrl ? (
          <div className="mt-4 rounded-xl border border-weave-teal/30 bg-weave-teal/10 p-5" data-testid="prospectus-success">
            <div className="flex items-start gap-3 text-weave-teal">
              <CheckCircle2 size={22} className="mt-0.5" />
              <div>
                <p className="font-medium text-weave-ink">Your download should begin automatically.</p>
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="prospectus-direct-download-link"
                  className="mt-2 inline-flex items-center gap-2 text-sm font-medium underline"
                >
                  <Download size={15} /> Open the prospectus again
                </a>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="prospectus-form">
            <input
              type="text"
              aria-label="First name (optional)"
              placeholder="First name (optional)"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              data-testid="prospectus-name-input"
              className="w-full rounded-xl border border-weave-line bg-white px-4 py-3 text-sm text-weave-ink placeholder:text-weave-mute focus:border-weave-teal focus:outline-none"
            />
            <input
              type="email"
              aria-label="Email address"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="prospectus-email-input"
              className="w-full rounded-xl border border-weave-line bg-white px-4 py-3 text-sm text-weave-ink placeholder:text-weave-mute focus:border-weave-teal focus:outline-none"
            />
            <label className="flex items-start gap-3 text-sm text-weave-slate leading-relaxed" data-testid="prospectus-consent-label">
              <input
                type="checkbox"
                checked={form.marketing_consent}
                onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })}
                data-testid="prospectus-consent-checkbox"
                className="mt-1 accent-weave-teal"
              />
              <span>Keep me updated about Personal Clarity and The Weave Founder Academy. Optional.</span>
            </label>
            <p className="text-xs text-weave-mute -mt-1">
              We’ll use your email to send the prospectus. Updates are only sent if you opt in.
            </p>
            <button
              type="submit"
              disabled={busy}
              data-testid="prospectus-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-weave-teal text-white hover:bg-weave-tealdeep transition-colors duration-300 rounded-full px-8 py-3.5 font-medium disabled:opacity-60"
            >
              <Download size={16} /> {busy ? "Preparing download…" : "Send me the prospectus"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
